/**
 * <brand-size-selector> — sélecteur de taille de la carte produit, carrousel comme grille
 * (product-card-brand.liquid), et de la carte compacte des suggestions (brand-cart-upsell-card.liquid).
 *
 * Ne porte AUCUN second mécanisme d'ajout au panier : au clic sur une valeur disponible, il se
 * contente de changer la valeur de l'input caché `name="id"` du formulaire d'ajout existant
 * (rendu par snippets/product-card-brand-add-form.liquid, chemin canonique décrit dans
 * docs/conventions.md §6 bis — `product-form-component` + `fetch(Theme.routes.cart_add_url)`).
 * Ce formulaire lit la valeur courante de l'input au moment du submit (`new FormData(form)`),
 * donc changer `.value` suffit : aucun événement de framework à émettre.
 *
 * Met aussi à jour, dans son propre périmètre, ce que la variante choisie change visuellement :
 * l'état pressé des boutons, le prix (ligne séparée ou prix intégré au bouton), et la
 * disponibilité du bouton d'ajout — chaque bouton de taille porte sa donnée déjà formatée côté
 * serveur (`data-price`, `data-compare-price`, `data-discount`), donc aucune logique de devise
 * n'est recalculée en JS. Le bouton d'ajout reçoit la taille et le prix choisis : c'est lui que lit
 * la ligne provisoire du panier (assets/brand-add-pending.js).
 *
 * Attribut `data-reveal` (carte encadrée de la routine) : les tailles restent repliées, le bouton
 * d'ajout les déplie à sa place, et le choix d'une taille l'ajoute au panier. « Échap » ou la
 * sortie du focus les replie. L'état déplié se lit sur `data-revealed`, le style est celui de la carte.
 */
class BrandSizeSelector extends HTMLElement {
  connectedCallback() {
    this.addEventListener('click', this.#handleClick);
    if (!this.#revealsOnDemand) return;

    this.addEventListener('keydown', this.#handleKeydown);
    this.addEventListener('focusout', this.#handleFocusOut);
    this.#submitButton?.setAttribute('aria-expanded', 'false');
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#handleClick);
    this.removeEventListener('keydown', this.#handleKeydown);
    this.removeEventListener('focusout', this.#handleFocusOut);
  }

  get #revealsOnDemand() {
    return this.hasAttribute('data-reveal');
  }

  get #submitButton() {
    const button = this.querySelector('[ref="submitButton"]');
    return button instanceof HTMLButtonElement ? button : null;
  }

  /** @param {MouseEvent} event */
  #handleClick = (event) => {
    const target = event.target instanceof Element ? event.target : null;
    const size = /** @type {HTMLElement | null} */ (target?.closest('.product-card-brand__size-btn') ?? null);

    if (size) {
      this.#chooseSize(size);
    } else if (this.#revealsOnDemand && target?.closest('[ref="submitButton"]')) {
      event.preventDefault();
      this.#reveal();
    }
  };

  /** @param {KeyboardEvent} event */
  #handleKeydown = (event) => {
    if (event.key !== 'Escape' || !this.hasAttribute('data-revealed')) return;

    event.stopPropagation();
    this.#collapse();
    this.#submitButton?.focus();
  };

  /** @param {FocusEvent} event */
  #handleFocusOut = (event) => {
    if (event.relatedTarget instanceof Node && this.contains(event.relatedTarget)) return;
    this.#collapse();
  };

  /** @param {HTMLElement} size */
  #chooseSize(size) {
    const variantId = size.dataset.variantId;
    if (size.hasAttribute('disabled') || !variantId) return;

    this.#selectButton(size);
    this.#updateVariantInput(variantId);
    this.#updatePrice(size);
    this.#updateSubmitButton(size);

    if (!this.#revealsOnDemand) return;

    const submitButton = this.#submitButton;
    this.#collapse();
    submitButton?.focus();
    submitButton?.form?.requestSubmit(submitButton);
  }

  #reveal() {
    this.setAttribute('data-revealed', '');
    this.#submitButton?.setAttribute('aria-expanded', 'true');

    const sizes = '.product-card-brand__size-btn:not(:disabled)';
    const first = this.querySelector(`${sizes}[aria-pressed="true"]`) ?? this.querySelector(sizes);
    if (first instanceof HTMLElement) first.focus();
  }

  #collapse() {
    this.removeAttribute('data-revealed');
    this.#submitButton?.setAttribute('aria-expanded', 'false');
  }

  /** @param {HTMLElement} selected */
  #selectButton(selected) {
    for (const button of this.querySelectorAll('.product-card-brand__size-btn')) {
      button.setAttribute('aria-pressed', button === selected ? 'true' : 'false');
    }
  }

  /** @param {string} variantId */
  #updateVariantInput(variantId) {
    const targetId = this.dataset.target;
    const input = targetId ? this.querySelector(`#${CSS.escape(targetId)}`) : null;
    if (input instanceof HTMLInputElement) input.value = variantId;
  }

  /** @param {HTMLElement} button */
  #updatePrice(button) {
    // La ligne de prix (`ref="priceLine"`) est un frère du sélecteur, pas un descendant : elle
    // vit dans product-card-brand.liquid, au-dessus de la zone d'action. On remonte à la racine
    // de la carte pour l'atteindre, plutôt que de dupliquer le prix dans le sélecteur.
    const card = this.closest('.product-card-brand, .brand-cart-upsell-card') || this;
    const price = button.dataset.price || '';
    const comparePrice = button.dataset.comparePrice || '';
    const discount = button.dataset.discount || '';
    const hasDiscount = discount !== '';

    const priceCurrent = card.querySelector('[ref="priceCurrent"]');
    if (priceCurrent) priceCurrent.textContent = price;

    const priceOld = card.querySelector('[ref="priceOld"]');
    if (priceOld) {
      priceOld.textContent = hasDiscount ? comparePrice : '';
      priceOld.hidden = !hasDiscount;
    }

    const priceDiscount = card.querySelector('[ref="priceDiscount"]');
    if (priceDiscount) {
      priceDiscount.textContent = hasDiscount ? discount : '';
      priceDiscount.hidden = !hasDiscount;
    }

    const addPrice = this.querySelector('[ref="addPrice"]');
    if (addPrice) addPrice.textContent = price;
  }

  /**
   * Une taille cliquable est disponible : le bouton d'ajout l'est aussi, et il porte la taille et le
   * prix que la ligne provisoire du panier affichera.
   *
   * @param {HTMLElement} size
   */
  #updateSubmitButton(size) {
    const submitButton = this.#submitButton;
    if (!submitButton) return;

    submitButton.disabled = false;
    submitButton.dataset.pendingVariant = size.dataset.value ?? '';
    submitButton.dataset.pendingPrice = size.dataset.price ?? '';
  }
}

if (!customElements.get('brand-size-selector')) {
  customElements.define('brand-size-selector', BrandSizeSelector);
}
